import React from 'react';
import { RefreshCw, Plus } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Role } from '../../../core/types';

interface ListingActionsProps {
  // Display name for the lesson type
  lessonTypeName: string;
  pluralName: string;

  // User role for permission checks
  userRole: Role;

  // Actions
  onRefresh: () => void;
  onCreate?: () => void;

  // Permissions
  canCreate?: boolean;

  // Loading states
  isRefreshing?: boolean;
}

/**
 * Action buttons for lesson listing pages
 * Includes refresh and create new lesson buttons with role-based visibility
 *
 * @example
 * <ListingActions
 *   lessonTypeName="Lecție"
 *   pluralName="Lecții"
 *   userRole={Role.ADMIN}
 *   onRefresh={refresh}
 *   onCreate={createLesson}
 *   canCreate={true}
 * />
 */
export const ListingActions: React.FC<ListingActionsProps> = ({
  lessonTypeName,
  pluralName,
  userRole,
  onRefresh,
  onCreate,
  canCreate = false,
  isRefreshing = false,
}) => {
  const isAdmin = [Role.ADMIN, Role.DBEST].includes(userRole);

  return (
    // `pt-18 md:pt-0`: sub `md` hamburgerul flotant (52x52 la 16,16, marginea de jos la 68) acoperea
    // titlul (y=7) si subtitlul (y=44) pe toate cele patru listari (masurat la 360 pe 63e5aa09).
    // Aceeasi valoare ca SimpleReportLayout; de la `md` hamburgerul nu mai exista.
    <div className="flex w-full flex-col items-center space-y-4 pt-18 pb-4 sm:flex-row sm:justify-between sm:space-x-4 sm:space-y-0 md:pt-0">
      {/* Left side - Title */}
      <div className="flex-1">
        <h1 className="text-2xl font-bold text-foreground">{pluralName}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gestionează {pluralName.toLowerCase()} din sistem
        </p>
      </div>

      {/* Right side - Actions */}
      <div className="flex w-full sm:w-auto gap-2">
        {/* Refresh Button */}
        <Button
          className="flex-1 sm:flex-initial sm:w-auto"
          variant="outline"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          <span>Reîncărcare</span>
        </Button>

        {/* Create New Button (Admin/DBEST only) */}
        {isAdmin && canCreate && onCreate && (
          <Button
            className="flex-1 sm:flex-initial sm:w-auto bg-primary hover:bg-blue-700 text-white"
            onClick={onCreate}
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>Adaugă {lessonTypeName}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default ListingActions;

